<template>
  <div class="kiosk-container-v2">
    <router-view ref="activeView" />
    <IdleTimeoutOverlay
      :show="showIdleOverlay"
      :seconds-left="countdownSeconds"
      @stay="cancelIdleCountdown"
      @leave="exitToHome"
    />
  </div>
</template>

<script>
import IdleTimeoutOverlay from "../views/KioskV2/components/IdleTimeoutOverlay.vue";

export default {
  name: "KioskLayoutV2",
  components: {
    IdleTimeoutOverlay,
  },
  data() {
    return {
      idleTimeout: null,
      countdownInterval: null,
      showIdleOverlay: false,
      countdownSeconds: 90,
      isExitingToHome: false,
      IDLE_THRESHOLD: 60000, // 60 seconds (default)
      IDLE_THRESHOLD_CHECKOUT: 300000, // 5 minutes — checkout page
      COUNTDOWN_INITIAL: 60, // 60 seconds
    };
  },
  computed: {
    isHomePage() {
      return this.$route.name === "KioskHomeV2";
    },
    onCheckout() {
      return this.$route.name === "OrderCheckoutV2";
    },
    // Give customers more breathing room (5 min) on the checkout page before
    // the 60-second cancel countdown kicks in. Everywhere else stays 60s.
    currentIdleThreshold() {
      return this.onCheckout ? this.IDLE_THRESHOLD_CHECKOUT : this.IDLE_THRESHOLD;
    },
  },
  mounted() {
    this.initActivityListeners();
    this.resetIdleTimer();
  },
  beforeDestroy() {
    this.removeActivityListeners();
    this.clearTimers();
  },
  watch: {
    $route() {
      // Reset everything on route change
      this.cancelIdleCountdown();
      this.resetIdleTimer();
    },
  },
  methods: {
    initActivityListeners() {
      const events = ["mousedown", "touchstart", "keydown", "scroll"];
      events.forEach((event) => {
        window.addEventListener(event, this.handleUserActivity);
      });
    },
    removeActivityListeners() {
      const events = ["mousedown", "touchstart", "keydown", "scroll"];
      events.forEach((event) => {
        window.removeEventListener(event, this.handleUserActivity);
      });
    },
    handleUserActivity() {
      if (this.showIdleOverlay) {
        // If they act while overlay is showing, hide it and reset
        this.cancelIdleCountdown();
      }
      this.resetIdleTimer();
    },
    resetIdleTimer() {
      this.clearIdleTimeout();

      // Don't start timer on Home Page
      if (this.isHomePage) return;

      this.idleTimeout = setTimeout(() => {
        this.startIdleCountdown();
      }, this.currentIdleThreshold);
    },
    startIdleCountdown() {
      this.showIdleOverlay = true;
      this.countdownSeconds = this.COUNTDOWN_INITIAL;

      this.countdownInterval = setInterval(() => {
        if (this.countdownSeconds > 0) {
          this.countdownSeconds--;
        } else {
          this.exitToHome();
        }
      }, 1000);
    },
    cancelIdleCountdown() {
      this.showIdleOverlay = false;
      this.clearCountdownInterval();
    },
    clearIdleTimeout() {
      if (this.idleTimeout) {
        clearTimeout(this.idleTimeout);
        this.idleTimeout = null;
      }
    },
    clearCountdownInterval() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
    clearTimers() {
      this.clearIdleTimeout();
      this.clearCountdownInterval();
    },
    async cancelActiveOrderOnIdle() {
      // Primary path: call cancelOrderProcessing directly on the rendered route
      // component (KioskOrderV2 / KioskCheckoutV2) via the router-view ref. This
      // is exactly what the manual Cancel button does.
      const activeView = this.$refs.activeView || null;
      const order = activeView && activeView.currentOrder;
      const items = order && Array.isArray(order.items) ? order.items : [];
      const hasRealOrder =
        order && ((order.id && order.id > 0) || (order.local_id && order.local_id > 0) || items.length > 0);

      if (
        activeView &&
        typeof activeView.cancelOrderProcessing === "function" &&
        hasRealOrder
      ) {
        try {
          await activeView.cancelOrderProcessing({ skipNavigation: true });
          return true;
        } catch (e) {
          console.error("[KioskIdle] cancelOrderProcessing threw:", e);
        }
      }

      // Fallback: ref didn't resolve — broadcast and let the mounted order
      // component cancel itself in its own context.
      this.$root.$emit("kiosk:idle-cancel");
      await new Promise((r) => setTimeout(r, 1200));
      return false;
    },
    async exitToHome() {
      if (this.isExitingToHome) return;
      this.isExitingToHome = true;
      this.clearTimers();
      this.showIdleOverlay = false;

      try {
        await this.cancelActiveOrderOnIdle();
      } catch (e) {
        console.error("[KioskIdle] cancelActiveOrderOnIdle failed:", e);
      }

      try {
        if (this.$route.name !== "KioskHomeV2") {
          await this.$router.push({ name: "KioskHomeV2" });
        }
      } catch (e) {
        // NavigationDuplicated — already navigated, ignore
      } finally {
        this.isExitingToHome = false;
      }
    },
  },
};
</script>

<style scoped>
.kiosk-container-v2 {
  width: 100%;
  height: 100vh;
  background-color: #f0f9ff;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
}

/* Fade in animation */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Hide scrollbar for kiosk feel */
.kiosk-container-v2 ::-webkit-scrollbar {
  display: none;
}

.kiosk-container-v2 {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
